import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { HeartHandshake, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentResult({ mode = "success" }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [state, setState] = useState(mode === "cancel" ? "cancel" : "checking");
  const started = useRef(false);

  useEffect(() => {
    if (mode === "cancel" || started.current) return;
    started.current = true;
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { await refreshMe(); setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {}
      if (++tries < 6) setTimeout(poll, 2000); else setState("pending");
    };
    poll();
  }, [mode, params, refreshMe]);

  const Card = ({ icon: Icon, color, title, text, btn, to }) => (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4">
      <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-10 max-w-md w-full text-center">
        <Icon size={56} className={`mx-auto mb-4 ${color}`} strokeWidth={2} />
        <h1 className="font-head text-2xl font-bold text-forest-900 mb-2">{title}</h1>
        <p className="text-forest-700 text-lg mb-6">{text}</p>
        <button onClick={() => navigate(to)} data-testid="payment-continue"
          className="min-h-[56px] px-8 rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-colors">{btn}</button>
      </div>
    </div>
  );

  if (state === "checking")
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream gap-4">
        <div className="w-16 h-16 rounded-full bg-terracotta flex items-center justify-center animate-pulse"><HeartHandshake className="text-white" size={32} strokeWidth={2.5} /></div>
        <p className="text-forest-700 text-lg font-medium">Confirming your payment…</p>
      </div>
    );
  if (state === "paid") return <Card icon={CheckCircle2} color="text-forest-700" title="Payment successful! 🎉" text="Your plan is now active and your account has been upgraded." btn="Go to Connect" to="/app" />;
  if (state === "cancel") return <Card icon={XCircle} color="text-forest-500" title="Checkout cancelled" text="No charge was made. You can choose a plan whenever you're ready." btn="Back to Plans" to="/app/plans" />;
  if (state === "failed") return <Card icon={XCircle} color="text-destructive" title="Payment failed" text="Something went wrong and no charge was completed. Please try again." btn="Back to Plans" to="/app/plans" />;
  if (state === "pending") return <Card icon={Clock} color="text-gold" title="Payment processing" text="This is taking a moment. Your plan will activate as soon as the payment clears." btn="Go to Connect" to="/app" />;
  return <Card icon={XCircle} color="text-destructive" title="Something went wrong" text="We couldn't find that checkout session." btn="Back to Plans" to="/app/plans" />;
}
